<template>
  <div>
    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <validation-observer ref="regionPiricingDetailForm">
        <b-form ref="regionPricingDetailFormElement" class="" @submit.prevent="submit">
          <b-row>
            <b-col sm="12" md="6">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="package_type">{{ t('Packaging Type') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Packaging Type')"
                  rules="required"
                >
                  <v-select
                    id="package_type"
                    v-model="values.packagingType"
                    :options="
                      packagingTypes.map(i => {
                        return {
                          text: i.info,
                          value: i.value
                        };
                      })
                    "
                    :placeholder="t('Make a Choice')"
                    label="text"
                    :tabindex="2"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>

                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col sm="12" md="6" v-if="values.packagingType && values.packagingType.value !== 3">
              <b-form-group>
                <div class="d-flex form-group-label">
                  <label for="package_value">{{ t('Package Type') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider
                  #default="{ errors }"
                  :name="t('Package Type')"
                  :rules="
                    values.packagingType && values.packagingType.value !== 3 ? 'required' : ''
                  "
                >
                  <v-select
                    id="package_value"
                    v-model="values.packageValue"
                    :options="
                      packageTypes.map(i => {
                        return {
                          text: i.info,
                          value: i.value
                        };
                      })
                    "
                    :placeholder="t('Make a Choice')"
                    label="text"
                    :tabindex="2"
                  >
                    <span slot="no-options">{{ t('No records found') }}</span>
                  </v-select>

                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
            <b-col cols="12" class="mb-1">
              <a class="download_xls" data="1" @click="downloadTemplate" href="#">{{
                $t('Download Cost Table Template')
              }}</a>
            </b-col>
            <b-col cols="12">
              <validation-provider
                #default="{ errors, validate }"
                :name="t('File')"
                rules="required"
                vid="file"
              >
                <drop-zone @filesSelected="handleFileSelect($event) || validate($event)" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-col>
            <b-col cols="12">
              <div
                class="d-flex gap-3 align-items-center justify-content-end mt-1"
                style="gap: 12px"
              >
                <b-button
                  variant="primary"
                  class="d-flex align-items-center"
                  type="submit"
                  :tabindex="5"
                >
                  {{ submitting ? t('Please Wait') : type === 'new' ? t('Add') : t('Save') }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-overlay>
  </div>
</template>
<script>
import { BCol, BForm, BFormGroup, BRow, BButton, BOverlay } from 'bootstrap-vue';
import DropZone from '@/components/DropZone.vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { togglePasswordVisibility } from '@core/mixins/ui/forms';
import { helper } from '@core/mixins/helper';
import useService from '@/auth/service/useService';
import vSelect from 'vue-select';

export default {
  components: {
    BOverlay,
    BButton,
    BFormGroup,
    BRow,
    BCol,
    BForm,
    DropZone,
    ValidationObserver,
    ValidationProvider,
    vSelect
  },

  mixins: [togglePasswordVisibility, helper],
  props: {
    type: {
      type: String,
      default: 'new'
    },
    submitting: {
      type: Boolean,
      default: false
    },
    values: {
      type: Object,
      default() {
        return {
          file: null,
          packageValue: '',
          packagingType: ''
        };
      }
    }
  },
  data() {
    return {
      statusTypeOptions: [],
      packageTypes: [],
      packagingTypes: []
    };
  },

  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon';
    }
  },

  async created() {
    try {
      this.packagingTypes = await useService.constantService
        .getProviderRegionPricePackageType()
        .then(res => {
          return res.data;
        });
    } finally {
      this.isLoading = false;
    }
  },

  methods: {
    handleFileSelect(files) {
      this.values.file = files[0];
    },
    submit() {
      this.$refs.regionPiricingDetailForm.validate().then(success => {
        if (success) {
          this.$emit('submit', {
            ...this.values,
            packageType: this.values.packagingType.value,
            packageValue: this.values.packageValue?.value
          });
        }
      });
    },

    downloadTemplate() {
      useService.providerService.downloadPricingTemplate(this.$route.params.id);
    },

    async getPackageTypes() {
      try {
        this.isLoading = true;
        if (this.values.packagingType && this.values.packagingType.value === 1) {
          this.packageTypes = await this.getConsignmentPackageType();
        } else if (this.values.packagingType && this.values.packagingType.value === 2) {
          this.packageTypes = await useService.providerService
            .getProviderPackageTypes(this.$route.params.id)
            .then(res => {
              return (res?.data || []).map(i => {
                return {
                  info: i.description,
                  value: i.packagingType
                };
              });
            });
        } else {
          this.packageTypes = [];
        }
      } finally {
        this.isLoading = false;
      }
    },

    resetForm() {
      this.$refs.regionPricingDetailFormElement.reset();
    }
  },
  watch: {
    'values.packagingType': {
      handler() {
        this.values.packageValue = '';
        this.getPackageTypes();
      }
    },
    packageType: {
      handler() {
        this.loadItems();
      }
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
